import {
  ArrowRight,
  CheckCircle,
  ClipboardText,
  Clock,
  Spinner,
} from "@phosphor-icons/react";
import React from "react";
import { Button } from "../ui/button";

export default function CuratedShortlistBanner({
  onClick,
  orderStatus,
  isLoadingStatus,
}) {
  // Every variant is a flex column, so when a parent stretches the card (e.g.
  // next to the Fundraising banner on /investors) the CTA drops to the bottom
  // instead of leaving an empty block under it.

  // paid_pending_intake → user paid but hasn't filled the intake form
  if (
    orderStatus?.has_active_order &&
    orderStatus.status === "paid_pending_intake"
  ) {
    return (
      <div className='relative flex flex-col w-full my-4 overflow-hidden text-white rounded-xl bg-gradient-to-tr from-green-600 to-emerald-500'>
        <div className='relative flex flex-col flex-1 p-5'>
          <div className='flex flex-col justify-between flex-1'>
            <div className='mb-4 lg:mb-0'>
              <p className='mb-3 text-[10px] font-medium tracking-wider uppercase opacity-80'>
                Action required
              </p>
              <h2 className='mb-3 text-[26px] font-medium tracking-tight leading-none font-display'>
                Complete your intake form to start your shortlist
              </h2>
              <p className='max-w-lg mt-3 text-sm opacity-75'>
                Payment received! Fill in your startup details so we can find
                the best-fit investors for you.
              </p>
            </div>
            <div className='flex-shrink-0 mt-4'>
              <Button
                onClick={onClick}
                className='w-full bg-white text-background hover:bg-white/90 font-semibold px-6 py-2.5 h-auto text-sm flex items-center gap-2'
              >
                <ClipboardText weight='bold' className='text-sm' />
                Complete Intake Form
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // intake_submitted | in_progress → order is being worked on
  if (
    orderStatus?.has_active_order &&
    ["intake_submitted", "in_progress"].includes(orderStatus.status)
  ) {
    return (
      <div className='relative flex flex-col w-full my-4 overflow-hidden text-white rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-500'>
        <div className='relative flex flex-col flex-1 p-5'>
          <div className='flex flex-col justify-between flex-1'>
            <div className='mb-4 lg:mb-0'>
              <p className='mb-3 text-[10px] font-medium tracking-wider uppercase opacity-80'>
                In progress
              </p>
              <h2 className='mb-3 text-[26px] font-medium tracking-tight leading-none font-display'>
                Your curated investor shortlist is being prepared
              </h2>
              <p className='max-w-lg mt-3 text-sm opacity-75'>
                Our team is researching and matching investors for your startup.
              </p>
            </div>
            <div className='flex items-center gap-2 mt-4 text-sm opacity-75'>
              <Clock weight='bold' className='text-base' />
              <span>You&apos;ll be notified when it&apos;s ready</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // fulfilled → shortlist delivered
  if (orderStatus?.has_active_order && orderStatus.status === "fulfilled") {
    return (
      <div className='relative flex flex-col w-full my-4 overflow-hidden text-white rounded-xl bg-gradient-to-tr from-green-600 to-teal-500'>
        <div className='relative flex flex-col flex-1 p-5'>
          <div className='flex flex-col justify-between flex-1'>
            <div className='flex items-center gap-2 mb-2'>
              <CheckCircle weight='fill' className='text-lg' />
              <p className='text-[10px] font-medium tracking-wider uppercase opacity-80'>
                Delivered
              </p>
            </div>
            <h2 className='mb-3 text-[22px] font-medium tracking-tight leading-none font-display'>
              Your curated investor shortlist has been delivered!
            </h2>
            <p className='max-w-lg text-sm opacity-75'>
              Check your email for the shortlist. Questions? Email{" "}
              <a
                href='mailto:partnerships@lucidityinsights.com'
                className='underline hover:opacity-100'
              >
                partnerships@lucidityinsights.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default — no order, show CTA to purchase
  return (
    <div className='relative flex flex-col w-full my-4 overflow-hidden text-white rounded-xl bg-gradient-to-tr from-purple-1 to-pink-1'>
      <div className='relative flex flex-col flex-1 p-5'>
        <div className='flex flex-col justify-between flex-1'>
          <div className='mb-4 lg:mb-0'>
            <p className='mb-3 text-[10px] font-medium tracking-wider uppercase opacity-80'>
              Founder service
            </p>
            <h2 className='mb-3 text-[26px] font-medium tracking-tight leading-none font-display'>
              Want us to do the investor research and matching for you?
            </h2>
            <p className='max-w-lg mt-3 text-sm opacity-75'>
              Get a curated investor shortlist delivered in{" "}
              <span className='font-semibold'>10 business days</span> and save
              weeks of work.
            </p>
          </div>
          <div className='flex-shrink-0 lg:mt-4'>
            <Button
              onClick={onClick}
              disabled={isLoadingStatus}
              className='w-full bg-white text-background hover:bg-white/90 font-medium px-6 py-2.5 h-auto text-sm flex items-center gap-2'
            >
              {isLoadingStatus ? (
                <Spinner className='text-sm animate-spin' />
              ) : (
                <>
                  Find out more
                  <ArrowRight weight='bold' className='text-sm' />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
